import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';
import { ref, Ref } from '@vue/composition-api';

Vue.use(VueRouter)

import { authorize } from './router-utils';
import { getStore } from './store/index';

import OidcCallback from './pages/oidc/OidcCallback.vue';
import Access from './pages/access.vue';
import AppsList from './pages/apps/apps-list.vue';
import AppVersionsList from './pages/apps/app-versions-list.vue';
import AppVersionSummary from './pages/apps/app-version-summary.vue';
import TextsEdit from './pages/apps/texts-edit.vue';
import Settings from './pages/settings/settings.vue';
import SectionEdit from './pages/settings/section-edit.vue';
import { signin, signinSilent } from './oidc';
import { checkToken, checkUserInfo } from './helpers/api';
import { promiseTimeout } from './helpers/utils';

const basePath = '/babel/';

const routes = [
    {
        path: '/',
        name: 'home',
        redirect: basePath + 'apps'
    },
    {
        path: '/access/callback',
        name: 'oidc-callback',
        component: OidcCallback
    },
    {
        path: '/access/callback-silent',
        name: 'oidc-callback-silent',
        component: OidcCallback
    },
    {
        path: basePath + 'access',
        name: 'access',
        component: Access
    },
    {
        path: basePath + 'apps',
        meta: { authorize: true },
        name: 'apps',
        component: AppsList,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getApps')) {
                next();
            }
        }
    },
    {
        path: basePath + 'apps/:appId/versions',
        meta: { authorize: true },
        name: 'app-versions',
        component: AppVersionsList,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getAppVersions', { appId: to.params.appId})) {
                next();
            }
        }
    },
    {
        path: basePath + 'apps/:appId/versions/:versionId/:lang/summary',
        meta: { authorize: true },
        name: 'app-version-summary',
        component: AppVersionSummary,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getAppVersionSummary', { versionId: to.params.versionId })) {
                next();
            }
        }
    },
    {
        path: basePath + 'apps/:appId/versions/:versionId/:lang/sections/:sectionId/texts',
        meta: { authorize: true },
        name: 'app-texts',
        component: TextsEdit,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getAppVersionSummary', { versionId: to.params.versionId })) {
                next();
            }
        }
    },
    // Settings routes
    {
        path: basePath + 'apps/:appId/versions/:versionId/settings',
        meta: { authorize: true, adminOnly: true },
        name: 'app-settings',
        component: Settings,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getAppVersionSummary', { versionId: to.params.versionId })) {
                next();
            }
        }
    },
    {
        path: basePath + 'apps/:appId/versions/:versionId/settings/:sectionId/edit',
        meta: { authorize: true, adminOnly: true },
        name: 'app-section-edit',
        component: SectionEdit,
        beforeEnter: async (to: Route, from: Route, next: Function) => {
            const store = getStore();
            if (await store.dispatch('getAppVersionSummary', { versionId: to.params.versionId })) {
                next();
            }
        }
    }
]

let router: VueRouter, route: Ref<Route>;

export function init() {
    let store = getStore();

    if (router == null) {
        router = new VueRouter({
            mode: 'history',
            routes,
            scrollBehavior: function(to, from, savedPosition) {
                const defaultScroll = { x: 0, y: 0 };
                if (to.path.includes('access/callback')) {
                    return defaultScroll;
                }
                else if (to.hash) {
                    return { selector: to.hash }
                }
                else if (savedPosition) {
                    return savedPosition
                }
                else {
                    return defaultScroll;
                }
            }
        });

        router.beforeEach(async (to: Route, from: Route, next: Function) => {
            route.value = to;

            async function check(args: any) {
                let token = await checkToken();

                if (token) {
                    store.commit('updateAuthentication', { result: 'OK', user: await checkUserInfo() });
                    return true;
                }
            }

            let authorize = to.matched.filter(x => !!x.meta.authorize).map(x => x.meta.authorize);

            if (authorize.length > 0) {
                let authorized = await check(authorize);
                if (!authorized) {
                    next({ name: 'access' });
                    return;
                }

                if (authorized && to.matched.filter(x => !!x.meta.access).length > 0) {
                    next({ name: 'apps' });
                    return;
                }

                if (authorized && to.matched.filter(x => !!x.meta.adminOnly).length > 0) {
                    if (!store.getters.hasRole('ADMIN')) {
                        next({ name: 'apps' });
                        return;
                    }
                }
            }

            if (to.name == 'access') {
                try {
                    if (await checkUserInfo()) {
                        next({ name: 'apps' });
                        return;
                    }
                }
                catch (err) {
                    console.error(err);
                }
            }

            document.title = 'Finder Babel';

            next();
        });

        route = ref<Route>(null);
    }

    return router;
}

export function useRouter() {
    return { router, route }
}
